import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * T025: useScanMessages hook.
 * Cycles through 20 scan progress messages at a 10-second interval.
 * Stops at the last message and does not loop (FR-016, FR-020, FR-021).
 */

const SCAN_MESSAGES = [
  'Connecting to your store…',
  'Fetching your product catalog…',
  'Analyzing product titles for AI readability…',
  'Checking product descriptions for depth and quality…',
  'Evaluating structured data (Schema.org markup)…',
  'Inspecting product images and alt text…',
  'Verifying product identifiers (GTIN, SKU, MPN)…',
  'Assessing taxonomy and categorization…',
  'Running AI text quality analysis…',
  'Checking for boilerplate content patterns…',
  'Measuring internal consistency across products…',
  'Evaluating product data completeness…',
  'Comparing against AI discoverability best practices…',
  'Analyzing how AI assistants interpret your products…',
  'Checking canonical URLs and Open Graph tags…',
  'Scoring media quality and image coverage…',
  'Reviewing product type and tag accuracy…',
  'Calculating sub-scores across 6 pillars…',
  'Assembling your AI Discoverability Score…',
  'Almost done — finalizing your results…',
] as const;

const MESSAGE_INTERVAL_MS = 10_000;
const FINAL_MESSAGE = 'Still processing… Final results will appear shortly.';

interface UseScanMessagesReturn {
  /** Current message to display */
  message: string;
  /** Current index (0-based, up to 20 for the final fallback) */
  messageIndex: number;
  /** Total messages in the rotation */
  totalMessages: number;
  /** Whether all messages have been shown */
  isComplete: boolean;
  /** Reset the message rotation to the beginning */
  reset: () => void;
}

export function useScanMessages(isActive: boolean): UseScanMessagesReturn {
  const [messageIndex, setMessageIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    setMessageIndex(0);
  }, [clearTimer]);

  useEffect(() => {
    if (!isActive) {
      clearTimer();
      return;
    }

    // Don't start timer if we've shown all messages
    if (messageIndex >= SCAN_MESSAGES.length) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setMessageIndex((prev) => {
        const next = prev + 1;
        // Stop at the end (index = length means we show the final message)
        if (next >= SCAN_MESSAGES.length) {
          clearTimer();
          return SCAN_MESSAGES.length;
        }
        return next;
      });
    }, MESSAGE_INTERVAL_MS);

    return clearTimer;
  }, [isActive, messageIndex, clearTimer]);

  const isComplete = messageIndex >= SCAN_MESSAGES.length;
  const message = isComplete
    ? FINAL_MESSAGE
    : SCAN_MESSAGES[messageIndex];

  return {
    message,
    messageIndex,
    totalMessages: SCAN_MESSAGES.length,
    isComplete,
    reset,
  };
}
